import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import PercentIcon from "@mui/icons-material/Percent";
import SnackBar from "../../components/SnackBar";
import { atualizarSelicApuracao } from "../../services/apuracao.service";
import { exibirCodRelatorio, fmtMesAno } from "./utils";
import { formatValue } from "../../utils/formatValue";
import styles from "./styles.module.css";

const ModalAtualizarSelicApuracao = ({
  open,
  onClose,
  onSuccess,
  onError,
  apuracao = null,
  idApuracao,
}) => {
  const [loading, setLoading] = useState(false);
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });

  const limpar = useCallback(() => {
    setObjSnackBar({ type: "", msg: "" });
  }, []);

  useEffect(() => {
    if (!open) {
      limpar();
    }
  }, [open, limpar]);

  const handleClose = () => {
    if (loading) return;
    onClose?.();
  };

  const handleConfirmar = async () => {
    if (!idApuracao) return;
    setLoading(true);
    try {
      const res = await atualizarSelicApuracao(idApuracao);
      if (res?.sucesso) {
        onSuccess?.(res.mensagem || "SELIC atualizada. Sumarização em processamento.");
        handleClose();
      } else {
        const msg = res?.mensagem || "Não foi possível atualizar a SELIC.";
        onError?.(msg);
        setObjSnackBar({ type: "error", msg });
      }
    } catch (e) {
      const msg = e?.message || "Erro ao solicitar atualização de SELIC.";
      onError?.(msg);
      setObjSnackBar({ type: "error", msg });
    } finally {
      setLoading(false);
    }
  };

  const cnpjFmt = formatValue("cnpj", apuracao?.cnpj);
  const empresa = apuracao?.empresa || "—";
  const tipo = exibirCodRelatorio(apuracao?.tipo);
  const periodo = `${fmtMesAno(apuracao?.periodoInicio)} – ${fmtMesAno(apuracao?.periodoFim)}`;

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <div className={styles.title}>
            <PercentIcon sx={{ marginRight: ".5rem" }} />
            Atualizar Selic
          </div>
        </DialogTitle>
        <DialogContent className={styles.content}>
          <div className={styles.readonlyBlock}>
            <span className={styles.readonlyLabel}>CNPJ — empresa</span>
            <Typography className={styles.readonlyValue}>
              {cnpjFmt} — {empresa}
            </Typography>
          </div>
          <div className={styles.readonlyBlock}>
            <span className={styles.readonlyLabel}>Tipo de apuração</span>
            <Typography className={styles.readonlyValue}>{tipo}</Typography>
          </div>
          <div className={styles.readonlyBlock}>
            <span className={styles.readonlyLabel}>Período de apuração</span>
            <Typography className={styles.readonlyValue}>{periodo}</Typography>
          </div>

          <Alert severity="info" sx={{ width: "100%" }}>
            Será executado o <strong>recálculo</strong> dos lançamentos desta apuração com a
            taxa SELIC vigente por competência, atualizando base Selic, PIS e COFINS. Os
            lançamentos contábeis de origem <strong>não são reimportados</strong>.
          </Alert>

          <Alert severity="warning" sx={{ width: "100%" }}>
            As marcações de <strong>compõe projeto</strong> são preservadas. Após confirmar,
            você será redirecionado à listagem até a sumarização concluir.
          </Alert>
        </DialogContent>

        <DialogActions className={styles.containerBtn}>
          <Button
            onClick={handleClose}
            variant="contained"
            className={styles.btnCancelar}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmar}
            variant="contained"
            className={styles.btnSalvar}
            disabled={loading}
          >
            {loading ? "Atualizando…" : "Atualizar Selic"}
          </Button>
        </DialogActions>
      </Dialog>

      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />
    </>
  );
};

export default ModalAtualizarSelicApuracao;
